import { useSyncExternalStore } from 'react'

/** The plan sections' root on the partner page (`P360Tabs`). */
const P360_TABS_ELEMENT_ID = 'P360Tabs'

const OPEN_EVENT = 'p360:plan-section-dialog-open'

/** Marks the dialog body a plan section is drawn in, so a scroll can be kept inside it. */
export const PLAN_SECTION_DIALOG_ATTRIBUTE = 'data-plan-section-dialog'

type PlanSectionDialogOpenDetail = {
  /** An element inside the section to bring into view once the dialog shows it. */
  targetId?: string
}

/**
 * On the cockpit page the plan sections are a bar pinned to the bottom of the screen and each
 * section opens in a dialog, so there is nothing on the page to scroll to.
 */
export const isPlanSectionsDocked = () =>
  document.getElementById(P360_TABS_ELEMENT_ID)?.dataset.docked === 'true'

export const requestPlanSectionDialog = (targetId?: string) => {
  window.dispatchEvent(
    new CustomEvent<PlanSectionDialogOpenDetail>(OPEN_EVENT, {
      detail: { targetId: targetId === P360_TABS_ELEMENT_ID ? undefined : targetId },
    })
  )
}

export const subscribePlanSectionDialog = (handler: (targetId?: string) => void) => {
  const listener = (event: Event) =>
    handler((event as CustomEvent<PlanSectionDialogOpenDetail>).detail?.targetId)

  window.addEventListener(OPEN_EVENT, listener)

  return () => window.removeEventListener(OPEN_EVENT, listener)
}

/** The room the cockpit's plan deck takes at the top of the screen over a full-screen dialog (an
 *  open plan section, the attention or documents tile opened over the whole screen); the dialog
 *  starts below it. */
export const PLAN_SECTION_DECK_ROOM_VAR = '--p360-plan-deck-room'

/** Marks the cockpit's plan deck over a full-screen dialog, so a panel opened from it opens there,
 *  over the dialog too. */
export const PLAN_SECTION_DECK_ATTRIBUTE = 'data-plan-section-deck'

/* The slots of the full-screen dialogs open now, the latest last: the deck stands over that one. */
let deckSlots: HTMLElement[] = []
const deckSlotListeners = new Set<() => void>()

const notifyDeckSlotListeners = () => deckSlotListeners.forEach((listener) => listener())

/** The ref of the element a full-screen dialog over the cockpit gives the plan deck, so the deck
 *  stands over the dialog's scrim rather than under it. The slot goes with the dialog. */
export const registerPlanSectionDeckSlot = (slot: HTMLElement | null) => {
  if (!slot) {
    return undefined
  }

  deckSlots = [...deckSlots.filter((it) => it !== slot), slot]
  notifyDeckSlotListeners()

  return () => {
    deckSlots = deckSlots.filter((it) => it !== slot)
    notifyDeckSlotListeners()
  }
}

const subscribePlanSectionDeckSlot = (listener: () => void) => {
  deckSlotListeners.add(listener)

  return () => {
    deckSlotListeners.delete(listener)
  }
}

const getPlanSectionDeckSlot = () => deckSlots.at(-1) ?? null

/** The slot of the full-screen dialog open over the cockpit, or `null` while none is open. */
export const usePlanSectionDeckSlot = () =>
  useSyncExternalStore(subscribePlanSectionDeckSlot, getPlanSectionDeckSlot)

/** Whether the element stands on the page itself rather than in a section the dialog shows. */
export const isOnPageOutsideSections = (element: Element | null) =>
  !!element &&
  element.id !== P360_TABS_ELEMENT_ID &&
  !element.closest(`#${P360_TABS_ELEMENT_ID}`) &&
  !element.closest(`[${PLAN_SECTION_DIALOG_ATTRIBUTE}]`)

/** Room left above an element scrolled to inside the dialog, which has no sticky page header. */
const SECTION_SCROLL_OFFSET = 16

/**
 * Scrolls the element into view wherever it stands: inside the section dialog the dialog's own
 * body scrolls, on the page the window does, by `offset` for the page's sticky header.
 */
export const scrollElementIntoPage = (
  element: Element,
  { offset = 0, behavior = 'auto' }: { offset?: number; behavior?: ScrollBehavior } = {}
) => {
  const container = element.closest<HTMLElement>(`[${PLAN_SECTION_DIALOG_ATTRIBUTE}]`)
  const elementTop = element.getBoundingClientRect().top

  if (container) {
    container.scrollTo({
      top:
        container.scrollTop +
        elementTop -
        container.getBoundingClientRect().top -
        SECTION_SCROLL_OFFSET,
      behavior,
    })

    return
  }

  window.scrollTo({ top: window.scrollY + elementTop - offset, behavior })
}
